import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LoadingSpinner } from '@/components/custom/erp';
import { EXCEL_OPERATION_EVENT, type ExcelOperationEventDetail } from './operation-events';

const APP_HEADER_HEIGHT = 42;
const TABLE_OVERLAY_SELECTOR = '[data-excel-operation-overlay-root]';

interface OverlayBounds {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export function ExcelOperationOverlay() {
  const [mounted, setMounted] = useState(false);
  const [bounds, setBounds] = useState<OverlayBounds | null>(null);
  const [operations, setOperations] = useState<Map<string, string>>(() => {
    if (typeof window === 'undefined') return new Map();

    const previewMode = new URLSearchParams(window.location.search).get('excel_loading_preview');
    if (!previewMode) return new Map();

    return new Map([
      ['preview', previewMode === 'export' ? 'Đang kết xuất dữ liệu Excel...' : 'Đang nhập dữ liệu từ Excel...']
    ]);
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleLoadingEvent = (event: Event) => {
      const detail = (event as CustomEvent<ExcelOperationEventDetail>).detail;
      const id = detail.id || 'default';

      setOperations(previous => {
        const next = new Map(previous);

        if (detail.loading) {
          next.set(id, detail.message || 'Đang xử lý Excel...');
        } else {
          next.delete(id);
        }

        return next;
      });
    };

    window.addEventListener(EXCEL_OPERATION_EVENT, handleLoadingEvent);

    return () => {
      window.removeEventListener(EXCEL_OPERATION_EVENT, handleLoadingEvent);
    };
  }, []);

  useEffect(() => {
    if (operations.size === 0) return;

    let animationFrame: number | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const updateBounds = () => {
      const target = document.querySelector<HTMLElement>(TABLE_OVERLAY_SELECTOR);

      if (!target) {
        setBounds(null);
        return;
      }

      const rect = target.getBoundingClientRect();
      setBounds({
        top: Math.max(APP_HEADER_HEIGHT, rect.top),
        right: Math.max(0, window.innerWidth - rect.right),
        bottom: Math.max(0, window.innerHeight - rect.bottom),
        left: Math.max(0, rect.left)
      });
    };

    const scheduleUpdateBounds = () => {
      if (animationFrame !== null) return;

      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = null;
        updateBounds();
      });
    };

    updateBounds();
    window.addEventListener('resize', scheduleUpdateBounds);
    window.addEventListener('scroll', scheduleUpdateBounds, true);

    const target = document.querySelector<HTMLElement>(TABLE_OVERLAY_SELECTOR);
    if (target && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleUpdateBounds);
      resizeObserver.observe(target);
    }

    return () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }

      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleUpdateBounds);
      window.removeEventListener('scroll', scheduleUpdateBounds, true);
    };
  }, [operations.size]);

  if (!mounted || operations.size === 0) return null;

  const message = Array.from(operations.values()).at(-1) || 'Đang xử lý Excel...';

  return createPortal(
    <div
      className='fixed z-[1301] bg-white backdrop-blur-md'
      style={{
        position: 'fixed',
        top: bounds?.top ?? APP_HEADER_HEIGHT,
        right: bounds?.right ?? 0,
        bottom: bounds?.bottom ?? 0,
        left: bounds?.left ?? 0,
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)'
      }}
    >
      <div className='flex h-full w-full flex-col items-center justify-center'>
        <LoadingSpinner size={40} />
        <p className='mt-4 text-sm font-semibold text-gray-700'>{message}</p>
      </div>
    </div>,
    document.body
  );
}
